import { useEffect, useRef } from "react";
import { useGameStateContext } from "../GameStateProvider";
import { CONSTANTS } from "../data";
import type { GameState } from "../types";

/**
 * Finds the recipe that produces a given item by searching through all recipes.
 * 
 * @param itemId - The item ID to find a recipe for
 * @returns Recipe entry [recipeId, recipe] or undefined if not found
 */
const findRecipeForItem = (itemId: string) => {
    return Object.entries(CONSTANTS.RECIPES).find(([, recipe]) => recipe.result.id === itemId);
};

/**
 * Recursively builds a crafting plan to create a target item.
 * This function analyzes the dependency chain of recipes needed to craft an item,
 * considering current inventory and available recipes.
 * 
 * The algorithm uses depth-first search with cycle detection to find the optimal
 * sequence of recipes to execute. It respects the maximum crafting depth to prevent
 * infinite recursion in complex recipe chains.
 * 
 * @param targetItemId - The item we want to craft
 * @param state - Current game state (for inventory checking)
 * @returns Array of recipe IDs in execution order, or null if impossible
 */
const getCraftingPlan = (targetItemId: string, state: GameState): string[] | null => {
    const plan: string[] = [];
    const visited = new Set<string>();

    const find = (item: string, depth: number): boolean => {
        // Prevent infinite recursion in complex recipe chains
        if (depth > CONSTANTS.AI_BARISTA.MAX_CRAFTING_DEPTH) {
            console.warn(`AI crafting plan exceeded max depth for ${item}`);
            return false;
        }

        // If we have it in inventory, we don't need to craft it
        if ((state.inventory[item] || 0) > 0) {
            return true;
        }

        // Find a recipe that produces this item
        const recipeEntry = findRecipeForItem(item);
        if (!recipeEntry) {
            // It's a base ingredient we don't have, can't craft
            return false;
        }

        // Cycle detection to prevent infinite loops
        if (visited.has(item)) {
            return false;
        }
        visited.add(item);

        const [recipeKey, recipe] = recipeEntry;
        // Recursively check if we can make all required ingredients
        for (const ingredient of recipe.ingredients) {
            if (!find(ingredient, depth + 1)) {
                return false;
            }
        }

        // If we can make all ingredients, add this recipe to the front of the plan
        plan.unshift(recipeKey);
        return true;
    };

    if (find(targetItemId, 0)) {
        return plan;
    }

    return null;
};

/**
 * AI Barista hook that automatically crafts and serves drinks for customers.
 * 
 * The AI operates in two phases:
 * 1. Direct serving: If a customer's order is already in inventory, serve it immediately
 * 2. Crafting: Find the next recipe in the crafting chain for pending orders
 * 
 * The AI respects its efficiency rating - higher efficiency means less ingredient consumption.
 * The AI speed determines how often it attempts actions.
 * 
 * The AI only operates when:
 * - The game screen is active
 * - AI Barista is enabled and purchased (level > 0)
 * - There are available brew stations
 * - There are customers to serve or ingredients to craft
 */
export const useAiBarista = () => {
    const { state, dispatch } = useGameStateContext();
    const { aiBarista, customers, inventory, brewStations, screen } = state;
    const isProcessing = useRef(false);

    useEffect(() => {
        // Only operate when game is active and AI is enabled
        if (screen !== 'game' || !aiBarista.active || aiBarista.level === 0) {
            return;
        }

        const interval = setInterval(() => {
            // Prevent concurrent processing to avoid race conditions
            if (isProcessing.current) return;
            isProcessing.current = true;

            // Phase 1: Find a customer to serve directly
            const customerToServe = customers.find(c => c.active && (inventory[c.order] || 0) > 0);
            if (customerToServe) {
                dispatch({ type: 'SERVE_CUSTOMER', customerId: customerToServe.id, itemId: customerToServe.order });
                isProcessing.current = false;
                return;
            }

            // Phase 2: Find a crafting task
            const stationIndex = brewStations.findIndex(s => s.slots[0] === null && s.slots[1] === null);
            if (stationIndex === -1) {
                isProcessing.current = false;
                return; // No free station available
            }

            // Build list of possible crafting actions
            const possibleActions = [];
            for (const customer of customers) {
                if (!customer.active) continue;

                const plan = getCraftingPlan(customer.order, state);
                if (plan && plan.length > 0) {
                    const recipeToCraftKey = plan[0];
                    const recipeToCraft = CONSTANTS.RECIPES[recipeToCraftKey];
                    const [ing1, ing2] = recipeToCraft.ingredients;

                    // Check if we have the required ingredients
                    if ((inventory[ing1] || 0) > 0 && (inventory[ing2] || 0) > 0) {
                        possibleActions.push({
                            customer,
                            recipeToCraftKey,
                            recipeToCraft,
                            ingredients: [ing1, ing2]
                        });
                    }
                }
            }

            if (possibleActions.length > 0) {
                // Prioritize the customer with the least patience (most urgent)
                possibleActions.sort((a, b) => a.customer.patience - b.customer.patience);
                const bestAction = possibleActions[0];
                
                const { recipeToCraftKey, recipeToCraft, ingredients } = bestAction;
                
                // Apply AI efficiency - chance to not consume ingredients
                const consumedItems = [];
                ingredients.forEach(ingId => {
                    const itemDef = CONSTANTS.ITEMS[ingId] || Object.values(CONSTANTS.RECIPES).find(r => r.result.id === ingId)?.result;
                    if (itemDef?.consumable) {
                        // AI Efficiency check - higher efficiency = less consumption
                        if (Math.random() > aiBarista.efficiency) {
                            consumedItems.push(ingId);
                        }
                    }
                });

                // Execute the crafting action
                dispatch({ type: 'ADD_TO_STATION', stationIndex, slotIndex: 0, item: ingredients[0] });
                dispatch({ type: 'ADD_TO_STATION', stationIndex, slotIndex: 1, item: ingredients[1] });
                dispatch({ type: 'BREW_SUCCESS', resultItem: recipeToCraft.result, consumedItems, quantity: 1 });
                
                // Discover recipe if not already known
                if (!state.discoveredRecipes.includes(recipeToCraftKey)) {
                    dispatch({ type: 'DISCOVER_RECIPE', recipeId: recipeToCraftKey });
                }
                
                // Clear the station for next use
                dispatch({ type: 'REMOVE_FROM_STATION', stationIndex, slotIndex: 0 });
                dispatch({ type: 'REMOVE_FROM_STATION', stationIndex, slotIndex: 1 });
            }

            isProcessing.current = false;
        }, aiBarista.speed);

        return () => clearInterval(interval);
    }, [aiBarista, customers, inventory, brewStations, screen, state.discoveredRecipes, dispatch, state]);
};
